import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

export interface ReinstateDecisionTarget {
  pickupOrderId: string;
  wasteType?: string | null;
  approvalStatus?: string | null;
}

/**
 * Undo a settled accept or reject.
 *
 * The order goes back to the queue and its sale is re-derived from the supplier
 * agreement, because rejecting overwrote the original price. If no agreement price is
 * found the order returns flagged rather than carrying a number nobody can justify --
 * which is why this warns rather than just confirming.
 */
export default function ReinstateDecisionDialog({
  open,
  submitting,
  target,
  error,
  onClose,
  onConfirm,
}: {
  open: boolean;
  submitting: boolean;
  target: ReinstateDecisionTarget | null;
  error: string | null;
  onClose: () => void;
  onConfirm: (reason: string) => void;
}) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (open) setReason('');
  }, [open]);

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle>{t('orderApprovals.reinstate.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2">
            {t('orderApprovals.reinstate.body', {
              wasteType: target?.wasteType ?? '',
              status: target?.approvalStatus ?? '',
            })}
          </Typography>
          <Alert severity="warning">{t('orderApprovals.reinstate.priceWarning')}</Alert>
          <TextField
            label={t('orderApprovals.reinstate.reason')}
            helperText={t('orderApprovals.reinstate.reasonHelp')}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            disabled={submitting}
            multiline
            minRows={2}
            inputProps={{ maxLength: 2000 }}
            fullWidth
          />
          {error ? <Alert severity="error">{error}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          {t('common.cancel')}
        </Button>
        <Button variant="contained" color="warning" disabled={submitting} onClick={() => onConfirm(reason)}>
          {submitting ? t('orderApprovals.reinstate.submitting') : t('orderApprovals.reinstate.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
